import { useEffect, useRef } from 'react';
import { t } from '../../i18n/idioma';
import { DURACAO_MARCA, desenharMarcaClique, MARCAS_CLIQUE, type MarcaClique } from '../../world/marcaClique';

/**
 * Escolha da marca do clique com a animação de cada uma (techspec-marca-do-clique §05): cada opção mostra o efeito
 * em loop sobre um pedaço de chão, para o jogador ver antes de escolher. Um só requestAnimationFrame para as quatro.
 */
const W = 132, H = 78, ESCALA = 1.4, PAUSA_MS = 450;

function chao(g: CanvasRenderingContext2D){
  g.fillStyle = '#26331f';
  g.fillRect(0, 0, W, H);
  // losangos do chão (44×22 na escala), para dar a noção do tile
  const tw = 44 * ESCALA, th = 22 * ESCALA;
  g.strokeStyle = 'rgba(0,0,0,.28)';
  g.lineWidth = 1;
  for (let j = -2; j <= 4; j++){
    for (let i = -2; i <= 3; i++){
      const cx = W / 2 + (i - j) * tw / 2, cy = H / 2 + 6 + (i + j) * th / 2;
      g.beginPath();
      g.moveTo(cx, cy - th / 2); g.lineTo(cx + tw / 2, cy); g.lineTo(cx, cy + th / 2); g.lineTo(cx - tw / 2, cy);
      g.closePath();
      g.stroke();
    }
  }
}

interface Props {
  valor: MarcaClique;
  onEscolher(m: MarcaClique): void;
}

export function MarcaCliqueOpcoes({ valor, onEscolher }: Props) {
  const telas = useRef<Array<HTMLCanvasElement | null>>([]);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const ctxs = telas.current.map(c => {
      if (!c) return null;
      c.width = W * dpr;
      c.height = H * dpr;
      const g = c.getContext('2d');
      g?.setTransform(dpr, 0, 0, dpr, 0, 0);
      return g;
    });
    const parado = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    const quadro = (now: number) => {
      MARCAS_CLIQUE.forEach((m, i) => {
        const g = ctxs[i];
        if (!g) return;
        chao(g);
        const dur = DURACAO_MARCA[m];
        const p = parado ? 0.4 : (now % (dur + PAUSA_MS)) / dur;
        if (p < 1) desenharMarcaClique(g, m, W / 2, H / 2 + 6, p, ESCALA);
      });
      if (!parado) raf = requestAnimationFrame(quadro);
    };
    raf = requestAnimationFrame(quadro);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="p-marca" role="radiogroup" aria-label={t('hud.cfg.marcaClique')}>
      {MARCAS_CLIQUE.map((id, i) => (
        <button key={id} type="button" role="radio" aria-checked={id === valor} onClick={() => onEscolher(id)}>
          <canvas ref={el => { telas.current[i] = el; }} width={W} height={H} aria-hidden="true" />
          <span>{t(`hud.cfg.marca.${id}`)}</span>
        </button>
      ))}
    </div>
  );
}
